import {
  BMComponentsType,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import IcBagSuitcaseLargeCurve16 from "@traveloka/icon-kit-web/react/IcBagSuitcaseLargeCurve16";
import IcSystemCheckmark16 from "@traveloka/icon-kit-web/react/IcSystemCheckmark16";
import { memo, useMemo } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { Pressable, StyleSheet } from "react-native";
import BMView from "../../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../../core/resource/MobileResourceProvider";
import { AdultPassengerFormValue, FlightPrebookFormValue } from "../../types";
import { FlightPrebookPassengerItemField } from "../types";

type Props = FlightPrebookPassengerItemField & {
  onClickEdit?: () => void;
};

type WatchValues = [
  string,
  boolean,
  "INFANT" | "CHILD" | "ADULT",
  AdultPassengerFormValue["addOns"] | undefined
];

function FlightPrebookPassengerField({ id, group, index, onClickEdit }: Props) {
  const { BMComponents } = useBMTheme();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);
  const { CorporateFlightPrebookForm: cr } = useMobileContentResource();

  const { control } = useFormContext<FlightPrebookFormValue>();
  const base = `passengers.${group}.${index}` as const;
  const [fullname, isEmployee, type, addOns] = useWatch({
    control,
    name: [
      `${base}.fullname`,
      `${base}.isEmployee`,
      `${base}.type`,
      `${base}.addOns`,
    ] as any,
  }) as WatchValues;

  const { errors } = useFormState({
    control,
    name: base,
  });
  const hasErrors = !!errors?.passengers?.[group]?.[index];

  const baggageInfos = useMemo(() => {
    if (type === "INFANT") return [];
    const passengerAddOns = (addOns as AdultPassengerFormValue["addOns"]) ?? [];
    return passengerAddOns
      .flat()
      .flatMap((a) => a.journeys)
      .flatMap((j) => (Array.isArray(j) ? j : [j]))
      .filter(Boolean);
  }, [type, addOns]);

  const displayName = useMemo(() => {
    if (fullname) return fullname;
    return `${cr[type]} ${index + 1}`;
  }, [fullname]);

  return (
    <BMView style={styles.container} spacing="sm">
      <BMView spacing="xs">
        <BMView row justify="between" align="center">
          <BMView row spacing="xs">
            <BMText variant="uiLarge">{displayName}</BMText>
            {!hasErrors ? (
              <IcSystemCheckmark16
                width={16}
                height={16}
                color={BMComponents.BMStyleIconInkHighlightGreen}
              />
            ) : null}
          </BMView>
          <Pressable onPress={onClickEdit}>
            <BMText variant="uiNormal" ink="BMStyleTextInkAction">
              {cr.ctaEditPassenger}
            </BMText>
          </Pressable>
        </BMView>
        {baggageInfos.length ? (
          <BMView row spacing="xs" flex style={styles.baggageInfo}>
            {baggageInfos.map((baggage, index) => (
              <BMView
                row
                spacing="xs"
                key={`baggage-wrapper-${id}-${fullname}-${index}`}
              >
                <BMView row align="center">
                  <IcBagSuitcaseLargeCurve16
                    width={12}
                    height={12}
                    color={BMComponents.BMStyleIconInkMuted}
                  />
                  <BMText variant="uiSmall" ink="BMStyleTextInkMuted">
                    {`${baggage.departureAirport.airportCode} - ${baggage.arrivalAirport.airportCode} `}
                    <BMText variant="uiSmallLi" ink="BMStyleTextInkSecondary">
                      {`${
                        Number(baggage.baggageQuantity) > 1
                          ? `${baggage.baggageQuantity} x`
                          : " "
                      }${baggage.baggageWeight} kg`}
                    </BMText>
                  </BMText>
                </BMView>
                {index < baggageInfos.length - 1 && (
                  <BMText variant="uiSmall" ink="BMStyleIconInkMuted">
                    •
                  </BMText>
                )}
              </BMView>
            ))}
          </BMView>
        ) : null}
      </BMView>
      {hasErrors ? (
        <BMText variant="paragraphSmall" ink="BMStyleIconInkHighlightRed">
          {isEmployee
            ? cr.employeeFieldErrorState
            : cr.nonEmployeeFieldErrorState}
        </BMText>
      ) : null}
    </BMView>
  );
}

export default memo(FlightPrebookPassengerField);

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    container: {
      padding: BMPrimitives.BMSize.md,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderRadius: BMPrimitives.BMRadius.md,
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
    },
    baggageInfo: {
      flexWrap: "wrap",
      rowGap: BMPrimitives.BMSize.xs,
    },
  });
}
